import { useState, memo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useDeleteAccount } from "@/hooks/useAuth";
import { DangerCard } from "./DangerCard";

type DeleteAccountProps = {
  className?: string;
  onSuccess?: () => void;
  disabled?: boolean;
};

export const DeleteAccount = memo(function DeleteAccount({
  className,
  onSuccess,
  disabled,
}: DeleteAccountProps) {
  const { t } = useLanguage();
  const { user } = useCurrentUser();
  const del = useDeleteAccount({ requirePassword: true });

  const [expanded, setExpanded] = useState(false);
  const [pass, setPass] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  if (!user) return null;

  const onDelete = async () => {
    if (!pass || del.isPending) return;
    setLocalError(null);
    try {
      await del.mutateAsync(pass);
      setExpanded(false);
      setPass("");
      onSuccess?.();
    } catch {
      setLocalError(t("deleteError"));
    }
  };

  return (
    <DangerCard
      className={className}
      title={t("deleteAccount")}
      description={t(
        "deleteAccountDesc"
      )}
      action={
        !expanded ? (
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setExpanded(true)}
            disabled={disabled || del.isPending}
          >
            {t("deleteAccount")}
          </Button>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setExpanded(false);
              setPass("");
              setLocalError(null);
            }}
            disabled={del.isPending}
          >
            {t("cancel")}
          </Button>
        )
      }
    >
      {expanded && (
        <div className="space-y-2">
          <Label htmlFor="deletePwd">{t("passwordToDelete")}</Label>
          <Input
            id="deletePwd"
            type="password"
            placeholder="••••••••"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            disabled={del.isPending}
          />
          <div className="flex gap-2">
            <Button
              variant="destructive"
              onClick={onDelete}
              disabled={!pass || del.isPending}
            >
              {del.isPending ? t("deleting") : t("confirmDelete")}
            </Button>
          </div>
          {localError && <p className="text-sm text-red-600">{localError}</p>}
        </div>
      )}
    </DangerCard>
  );
});
